import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FraudPatternAnalysis = ({ geographicData, paymentMethodData }) => {
  const [activeTab, setActiveTab] = useState('geographic');

  const getIntensityColor = (value) => {
    if (value >= 80) return 'bg-error';
    if (value >= 60) return 'bg-warning';
    if (value >= 40) return 'bg-accent';
    return 'bg-success';
  };

  const getVulnerabilityColor = (level) => {
    switch (level) {
      case 'critical':
        return 'text-error bg-error/10';
      case 'high':
        return 'text-warning bg-warning/10';
      case 'medium':
        return 'text-accent bg-accent/10';
      default:
        return 'text-success bg-success/10';
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">
            Analisis Pola Fraud
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Identifikasi pola dan tren aktivitas mencurigakan
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('geographic')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
              activeTab === 'geographic' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <Icon name="MapPin" size={16} className="inline-block mr-2" />
            Geografis
          </button>
          <button
            onClick={() => setActiveTab('payment')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
              activeTab === 'payment' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <Icon name="CreditCard" size={16} className="inline-block mr-2" />
            Metode Pembayaran
          </button>
        </div>
      </div>
      {activeTab === 'geographic' && (
        <div>
          <div className="mb-6">
            <div className="aspect-video bg-muted rounded-xl overflow-hidden mb-4">
              <iframe
                width="100%"
                height="100%"
                loading="lazy"
                title="Fraud Geographic Distribution"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=-6.2088,106.8456&z=10&output=embed"
                className="border-0"
              />
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Intensitas Rendah</span>
              <div className="flex gap-1">
                <div className="w-6 h-3 bg-success rounded"></div>
                <div className="w-6 h-3 bg-accent rounded"></div>
                <div className="w-6 h-3 bg-warning rounded"></div>
                <div className="w-6 h-3 bg-error rounded"></div>
              </div>
              <span className="text-muted-foreground">Intensitas Tinggi</span>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-foreground mb-3">
              Wilayah Berisiko Tinggi
            </h4>
            {geographicData?.map((location) => (
              <div
                key={location?.id}
                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${getIntensityColor(location?.riskLevel)}`}></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{location?.city}</p>
                    <p className="text-xs text-muted-foreground">{location?.province}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-error">{location?.fraudCount}</p>
                  <p className="text-xs text-muted-foreground">kasus</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {activeTab === 'payment' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="p-4 bg-muted/50 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="TrendingUp" size={16} className="text-error" />
                <span className="text-xs text-muted-foreground">Metode Paling Rentan</span>
              </div>
              <p className="text-lg font-semibold text-foreground">Kartu Kredit</p>
              <p className="text-xs text-error">+34% fraud rate</p>
            </div>
            <div className="p-4 bg-muted/50 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Shield" size={16} className="text-success" />
                <span className="text-xs text-muted-foreground">Metode Paling Aman</span>
              </div>
              <p className="text-lg font-semibold text-foreground">Virtual Account</p>
              <p className="text-xs text-success">-12% fraud rate</p>
            </div>
          </div>

          <h4 className="text-sm font-semibold text-foreground mb-3">
            Matriks Kerentanan Metode Pembayaran
          </h4>
          <div className="space-y-3">
            {paymentMethodData?.map((method) => (
              <div key={method?.id} className="border border-border rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name={method?.icon} size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{method?.name}</p>
                      <p className="text-xs text-muted-foreground">{method?.transactionCount} transaksi</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${getVulnerabilityColor(method?.vulnerability)}`}>
                    {method?.vulnerability === 'critical' ? 'Kritis' : method?.vulnerability === 'high' ? 'Tinggi' : method?.vulnerability === 'medium' ? 'Sedang' : 'Rendah'}
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Tingkat Fraud</span>
                    <span className="font-semibold text-error">{method?.fraudRate}%</span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-error rounded-full transition-smooth"
                      style={{ width: `${method?.fraudRate}%` }}
                    ></div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-2">Pola Fraud Umum:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {method?.commonPatterns?.map((pattern, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning rounded text-xs"
                      >
                        <Icon name="AlertTriangle" size={10} />
                        {pattern}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FraudPatternAnalysis;